import { useEffect, useState } from "react";
import { Bell, Loader2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-gf-bg border border-white/15 px-3.5 py-2.5 font-body text-sm text-gf-text placeholder:text-gf-muted focus:border-gf-primary focus:outline-none";

const Item = ({ it }) => (
  <span data-testid={`ticker-item-${it.symbol}`} className="inline-flex items-center gap-2 px-6 whitespace-nowrap">
    <span className="font-mono text-[10px] uppercase tracking-wider text-gf-muted">{it.label}</span>
    <span className="font-body text-[13px] font-bold text-gf-text">{it.last.toLocaleString("en-IN")} <span className="font-mono text-[9px] text-gf-muted">{it.unit}</span></span>
    <span className={`font-mono text-[10px] font-bold ${it.chg_pct > 0 ? "text-gf-primary" : it.chg_pct < 0 ? "text-red-400" : "text-gf-muted"}`}>
      {it.chg_pct > 0 ? "▲" : it.chg_pct < 0 ? "▼" : "—"} {Math.abs(it.chg_pct).toFixed(2)}%
    </span>
    {it.live ? (
      <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase text-gf-primary"><span className="w-1.5 h-1.5 rounded-full bg-gf-primary animate-pulse" />Live</span>
    ) : (
      <span className="font-mono text-[8px] uppercase text-amber-400/80 border border-amber-400/30 px-1 py-0.5">Market closed</span>
    )}
  </span>
);

const AlertModal = ({ items, onClose }) => {
  const [f, setF] = useState({ email: "", symbol: items[0]?.symbol || "", direction: "below", target: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const cur = items.find((i) => i.symbol === f.symbol);
  const submit = async (e) => {
    e.preventDefault();
    const target = parseFloat(f.target);
    if (!target || target <= 0) { toast.error("Enter a valid target price"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/commodity-alerts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, target }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Something went wrong");
      setDone(true);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ticker-alert-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-gf-surface border border-gf-primary/25 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-heading font-bold text-gf-text text-lg flex items-center gap-2"><Bell className="w-4 h-4 text-gf-primary" /> Set a price alert</p>
          <button onClick={onClose} data-testid="ticker-alert-close" className="text-gf-muted hover:text-gf-primary"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="text-center py-8" data-testid="ticker-alert-success">
            <CheckCircle2 className="w-10 h-10 text-gf-primary mx-auto" />
            <p className="font-body text-sm text-gf-text mt-3">Alert set. We'll email you the moment the market crosses your target.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="ticker-alert-form">
            <select data-testid="ticker-alert-symbol" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })} className={`${inputCls} appearance-none`}>
              {items.map((i) => <option key={i.symbol} value={i.symbol} className="bg-gf-bg">{i.label} — now {i.last.toLocaleString("en-IN")} {i.unit}</option>)}
            </select>
            <div className="flex gap-2.5">
              <select data-testid="ticker-alert-direction" value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className={`${inputCls} w-36 appearance-none`}>
                <option value="below" className="bg-gf-bg">Falls below</option>
                <option value="above" className="bg-gf-bg">Rises above</option>
              </select>
              <input required type="number" step="any" min="1" data-testid="ticker-alert-target" placeholder={cur ? `Target ${cur.unit}` : "Target price"}
                value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} className={inputCls} />
            </div>
            <input required type="email" data-testid="ticker-alert-email" placeholder="Your email *" value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
            <button type="submit" disabled={busy} data-testid="ticker-alert-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gf-primary text-gf-bg font-body text-sm font-bold px-5 py-3 hover:bg-gf-primary-hover transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Watch this price <Bell className="w-3.5 h-3.5" /></>}
            </button>
            <p className="font-body text-[10px] text-gf-muted text-center">Checked every 15 minutes against live exchange quotes. One email per alert — no spam.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export const CommodityTicker = () => {
  const [d, setD] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  useEffect(() => {
    const load = () => fetch(`${API}/public/commodity-ticker`).then((r) => r.json()).then(setD).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!d?.items?.length) return null;
  return (
    <section className="border-t border-b border-white/10 bg-gf-surface/60 py-2 overflow-hidden" data-testid="commodity-ticker">
      <style>{`@keyframes gf-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      <div className="flex w-max" style={{ animation: "gf-ticker 55s linear infinite", willChange: "transform" }}>
        {[0, 1].map((k) => (
          <div key={k} className="flex items-center" aria-hidden={k === 1}>
            {d.items.map((it) => <Item key={`${k}-${it.symbol}`} it={it} />)}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-3 mt-1.5 px-4">
        <p data-testid="ticker-source" className="font-mono text-[8px] text-gf-muted text-center">
          {d.source} · {d.note} Refreshed every minute.
        </p>
        <button onClick={() => setAlertOpen(true)} data-testid="ticker-alert-open"
          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-gf-primary border border-gf-primary/30 px-2 py-1 hover:bg-gf-primary hover:text-gf-bg transition-colors shrink-0">
          <Bell className="w-2.5 h-2.5" /> Price alert
        </button>
      </div>
      {alertOpen && <AlertModal items={d.items} onClose={() => setAlertOpen(false)} />}
    </section>
  );
};
